class HeroLayers extends HTMLElement {
  constructor() {
    super();
    this.rafId = null;
    this.lastSettle = 0;
    this.inView = false;
    this.scrollQueued = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    this.scrollProgress = 0;
    this.gap = 120;
    this.drag = {
      active: false,
      index: -1,
      pointerId: null,
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
      captured: false,
    };
    this.layerDrag = [];
    this.hitMaps = new Map();
    this.hitAlpha = 112;
    this.ignorePointerFromTouch = false;
    this.physics = {
      dragK: 280,
      dragC: 26,
      followNear: 0.42,
      followFar: 0.16,
      repulsion: 640,
    };
    this.homeTween = null;
    this.floatShift = 0;
    this.floatHold = null;
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.settle = this.settle.bind(this);
  }

  connectedCallback() {
    this.stack = this.querySelector('[data-hero-layers-stack]');
    this.layers = Array.from(this.querySelectorAll('[data-hero-layers-layer]'));
    this.section = this.closest('.shopify-section') || this;

    if (!this.stack || this.layers.length === 0) return;

    this.enableFloat = this.dataset.enableFloat !== 'false';
    this.enableDrag = this.dataset.enableDrag !== 'false';
    this.enableMouse = this.dataset.enableMouse !== 'false' && !this.isTouch && !this.enableDrag;
    this.enableScrollExit = this.dataset.enableScrollExit !== 'false';
    this.floatAmplitude = parseFloat(this.dataset.floatAmplitude || '10');
    this.mouseIntensity = parseFloat(this.dataset.mouseIntensity || '12');
    this.scrollSpread = parseFloat(this.dataset.scrollSpread || '80');
    this.scrollExitDistance = parseFloat(this.dataset.scrollExitDistance || '240');
    this.dragLimit = parseFloat(this.dataset.dragLimit || '30');
    this.readGap();
    this.layerDrag = this.layers.map((layer, index) => this.createLayerState(layer, index));
    this.applyBasePositions();

    if (this.reducedMotion) {
      this.classList.add('is-reduced');
      this.section.classList.add('is-reduced');
      return;
    }

    if (this.enableFloat) {
      this.style.setProperty('--hero-layers-float', `${this.floatAmplitude}px`);
      this.section.style.setProperty('--hero-layers-float', `${this.floatAmplitude}px`);
    }

    if (this.enableScrollExit) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('touchmove', this.onScroll, { passive: true });
      this.updateScrollExit();
    }

    if (this.enableMouse || this.enableDrag) {
      window.addEventListener('pointermove', this.onPointerMove, { passive: false });
    }

    if (this.enableDrag) {
      this.layers.forEach((layer, index) => {
        layer.classList.add('is-draggable');
        layer.dataset.layerIndex = String(index);
        const img = layer.querySelector('img, .hero-layers__visual');
        if (img) {
          img.setAttribute('draggable', 'false');
          if (img.complete && img.naturalWidth) this.prepareHitMap(layer, img);
          else img.addEventListener('load', () => this.prepareHitMap(layer, img), { once: true });
        }
        layer.addEventListener('pointerdown', this.onPointerDown);
        layer.addEventListener('touchstart', this.onTouchStart, { passive: false });
      });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
      window.addEventListener('touchmove', this.onTouchMove, { passive: false });
      window.addEventListener('touchend', this.onTouchEnd);
      window.addEventListener('touchcancel', this.onTouchEnd);
    }

    window.addEventListener('resize', this.onResize, { passive: true });
    this.observeVisibility();
  }

  disconnectedCallback() {
    this.stopSettle();
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('touchend', this.onTouchEnd);
    window.removeEventListener('touchcancel', this.onTouchEnd);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('touchmove', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.layers.forEach((layer) => {
      layer.removeEventListener('pointerdown', this.onPointerDown);
      layer.removeEventListener('touchstart', this.onTouchStart);
    });
    this.hitMaps.clear();
    if (this.io) this.io.disconnect();
  }

  createLayerState(layer, index) {
    const depth = parseFloat(layer.dataset.depth || String(3 - index));
    return {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      tx: 0,
      ty: 0,
      restX: 0,
      restY: this.baseYForDepth(depth),
      depth,
    };
  }

  onResize() {
    this.readGap();
    this.applyBasePositions();
    if (this.enableScrollExit) this.updateScrollExit();
  }

  readGap() {
    const raw = getComputedStyle(this.section).getPropertyValue('--hero-layers-gap').trim();
    const parsed = parseFloat(raw);
    this.gap = Number.isFinite(parsed) ? parsed : 120;
  }

  baseYForDepth(depth) {
    if (depth >= 3) return -this.gap;
    if (depth <= 1) return this.gap;
    return 0;
  }

  applyBasePositions() {
    this.homeTween = null;
    this.layers.forEach((layer, index) => {
      const depth = parseFloat(layer.dataset.depth || String(3 - index));
      const baseY = this.baseYForDepth(depth);
      layer.style.setProperty('--layer-base-y', `${baseY}px`);
      layer.style.setProperty('--parallax-x', '0px');
      layer.style.setProperty('--parallax-y', '0px');
      layer.style.setProperty('--scroll-y', '0px');
      layer.style.setProperty('--drag-x', '0px');
      layer.style.setProperty('--drag-y', '0px');
      this.clearLayerTransform(layer);
      const state = this.layerDrag[index];
      if (state) {
        state.x = 0;
        state.y = 0;
        state.vx = 0;
        state.vy = 0;
        state.tx = 0;
        state.ty = 0;
        state.restX = 0;
        state.restY = baseY;
        state.depth = depth;
      }
    });
  }

  prepareHitMap(layer, img) {
    if (!img || !img.naturalWidth) return;
    const maxW = 220;
    const scale = Math.min(1, maxW / img.naturalWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    try {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      this.hitMaps.set(layer, {
        data: ctx.getImageData(0, 0, canvas.width, canvas.height).data,
        width: canvas.width,
        height: canvas.height,
      });
    } catch (err) {
      this.hitMaps.delete(layer);
    }
  }

  hitsMappedArea(layer, clientX, clientY) {
    const rect = layer.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    if (width < 2 || height < 2) return false;

    const nx = (clientX - rect.left) / width;
    const ny = (clientY - rect.top) / height;
    if (nx < 0 || ny < 0 || nx > 1 || ny > 1) return false;

    const map = this.hitMaps.get(layer);
    if (map) {
      const x = Math.min(map.width - 1, Math.max(0, (nx * map.width) | 0));
      const y = Math.min(map.height - 1, Math.max(0, (ny * map.height) | 0));
      return map.data[(y * map.width + x) * 4 + 3] >= this.hitAlpha;
    }

    if (layer.classList.contains('hero-layers__placeholder')) return true;

    const dx = Math.abs(nx - 0.5) / 0.42;
    const dy = Math.abs(ny - 0.46) / 0.38;
    return dx + dy <= 1;
  }

  hitLayer(clientX, clientY) {
    const ordered = this.layers.slice().sort((a, b) => {
      return parseFloat(b.dataset.depth || '0') - parseFloat(a.dataset.depth || '0');
    });
    for (let i = 0; i < ordered.length; i += 1) {
      if (this.hitsMappedArea(ordered[i], clientX, clientY)) return ordered[i];
    }
    return null;
  }

  observeVisibility() {
    if (typeof IntersectionObserver === 'undefined') {
      this.inView = true;
      this.classList.add('is-inview');
      if (this.enableFloat) this.startSettle();
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          this.classList.toggle('is-inview', entry.isIntersecting);
          if (entry.isIntersecting) this.startSettle();
          else this.stopSettle();
        });
      },
      { threshold: 0.01, rootMargin: '80px 0px 80px 0px' }
    );
    this.io.observe(this.section);

    const rect = this.section.getBoundingClientRect();
    this.inView = rect.bottom > 0 && rect.top < window.innerHeight;
    this.classList.toggle('is-inview', this.inView);
    if (this.inView && this.enableFloat) this.startSettle();
  }

  onScroll() {
    if (!this.inView || this.scrollQueued) return;
    this.scrollQueued = true;
    requestAnimationFrame(() => {
      this.scrollQueued = false;
      this.updateScrollExit();
    });
  }

  updateScrollExit() {
    if (!this.enableScrollExit) return;
    const rect = this.section.getBoundingClientRect();
    const traveled = Math.max(0, -rect.top);
    this.scrollProgress = Math.min(1, traveled / Math.max(this.scrollExitDistance, 1));
    const scroll = this.scrollProgress;
    const exitSpread = scroll * this.scrollSpread;

    this.stack.style.opacity = String(1 - scroll);
    this.stack.style.webkitTransform = `scale(${1 - scroll * 0.12})`;
    this.stack.style.transform = `scale(${1 - scroll * 0.12})`;

    this.layers.forEach((layer, index) => {
      const depth = parseFloat(layer.dataset.depth || String(3 - index));
      layer.style.setProperty('--scroll-y', `${(2 - depth) * exitSpread}px`);
    });
  }

  magnetClamp(value) {
    const limit = this.dragLimit;
    if (limit <= 0) return 0;
    return limit * Math.tanh(value / limit);
  }

  eventPoint(event) {
    if (event.touches && event.touches[0]) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY, id: event.touches[0].identifier };
    }
    if (event.changedTouches && event.changedTouches[0]) {
      return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY, id: event.changedTouches[0].identifier };
    }
    return { x: event.clientX, y: event.clientY, id: event.pointerId };
  }

  setLayerTransform(layer, x, y) {
    const value = `translate(-50%, -50%) translate3d(${x}px, ${y}px, 0)`;
    layer.style.webkitTransform = value;
    layer.style.transform = value;
  }

  clearLayerTransform(layer) {
    layer.style.webkitTransform = '';
    layer.style.transform = '';
  }

  followStrength(fromDepth, toDepth) {
    const delta = Math.abs(fromDepth - toDepth);
    if (delta <= 1) return this.physics.followNear;
    return this.physics.followFar;
  }

  floatClock(now) {
    if (this.floatHold != null) return this.floatHold;
    return now - this.floatShift;
  }

  pauseFloat(now) {
    if (this.floatHold != null) return;
    this.floatHold = this.floatClock(now);
  }

  resumeFloat(now) {
    if (this.floatHold == null) return;
    this.floatShift = now - this.floatHold;
    this.floatHold = null;
  }

  floatY(index, now) {
    if (!this.enableFloat || this.reducedMotion) return 0;
    const layer = this.layers[index];
    const depth = parseFloat(layer?.dataset.depth || String(3 - index));
    const period = depth >= 3 ? 4800 : depth <= 1 ? 6000 : 5400;
    const delay = depth >= 3 ? 0 : depth <= 1 ? -2100 : -1200;
    const progress = ((this.floatClock(now) + delay) / period) % 1;
    const phase = progress < 0 ? progress + 1 : progress;
    return -this.floatAmplitude * 0.5 * (1 - Math.cos(phase * Math.PI * 2));
  }

  beginDrag(layer, point, event) {
    const index = parseInt(layer.dataset.layerIndex, 10);
    if (Number.isNaN(index) || !this.layerDrag[index]) return;

    const isTouch = event.type === 'touchstart' || event.pointerType === 'touch';
    if (!isTouch && event.cancelable) event.preventDefault();

    this.homeTween = null;
    this.pauseFloat(performance.now());

    this.drag.active = true;
    this.drag.captured = !isTouch;
    this.drag.index = index;
    this.drag.pointerId = point.id;
    this.drag.startX = point.x;
    this.drag.startY = point.y;
    this.drag.originX = this.layerDrag[index].x;
    this.drag.originY = this.layerDrag[index].y;

    if (!isTouch) layer.classList.add('is-dragging');
    if (event.pointerId != null && typeof event.pointerId === 'number' && layer.setPointerCapture) {
      try {
        layer.setPointerCapture(event.pointerId);
      } catch (err) {
        // Ignore capture errors on older WebKit.
      }
    }

    this.startSettle();
  }

  onTouchStart(event) {
    this.ignorePointerFromTouch = true;
    this.onPointerDown(event);
  }

  onTouchMove(event) {
    if (!this.drag.active) return;
    this.onPointerMove(event);
  }

  onTouchEnd(event) {
    this.onPointerUp(event);
    this.ignorePointerFromTouch = false;
  }

  onPointerDown(event) {
    if (!this.enableDrag || this.reducedMotion || !this.inView) return;
    if (event.type === 'pointerdown' && this.ignorePointerFromTouch) return;
    if (typeof event.button === 'number' && event.button > 0) return;

    const point = this.eventPoint(event);
    const layer = this.hitLayer(point.x, point.y);
    if (!layer) return;

    this.beginDrag(layer, point, event);
  }

  onPointerMove(event) {
    if (!this.inView) return;
    if (event.type === 'pointermove' && this.ignorePointerFromTouch) return;

    if (this.drag.active) {
      const point = this.eventPoint(event);
      if (this.drag.pointerId != null && point.id != null && point.id !== this.drag.pointerId) return;

      const dx = point.x - this.drag.startX;
      const dy = point.y - this.drag.startY;
      if (!this.drag.captured) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (this.isTouch && Math.abs(dy) > Math.abs(dx) + 6) {
          this.releaseDrag(false);
          return;
        }
        this.drag.captured = true;
        this.layers[this.drag.index]?.classList.add('is-dragging');
      }

      if (event.cancelable) event.preventDefault();

      const rawX = this.drag.originX + (point.x - this.drag.startX);
      const rawY = this.drag.originY + (point.y - this.drag.startY);
      const held = this.layerDrag[this.drag.index];
      held.tx = this.magnetClamp(rawX);
      held.ty = this.magnetClamp(rawY);

      this.layerDrag.forEach((state, index) => {
        if (index === this.drag.index) return;
        const follow = this.followStrength(state.depth, held.depth);
        state.tx = held.x * follow;
        state.ty = held.y * follow;
      });

      this.startSettle();
      return;
    }

    if (this.enableDrag) {
      const point = this.eventPoint(event);
      const hot = this.hitLayer(point.x, point.y);
      this.layers.forEach((layer) => {
        layer.classList.toggle('is-hot', layer === hot);
      });
    }

    if (!this.enableMouse) return;

    const x = (event.clientX / window.innerWidth - 0.5) * 2;
    const y = (event.clientY / window.innerHeight - 0.5) * 2;
    this.mouse.tx = Math.max(-1, Math.min(1, x));
    this.mouse.ty = Math.max(-1, Math.min(1, y));
    this.startSettle();
  }

  onPointerUp(event) {
    if (!this.drag.active) return;

    const point = this.eventPoint(event);
    if (this.drag.pointerId != null && point.id != null && point.id !== this.drag.pointerId) return;

    this.releaseDrag(true, event);
  }

  releaseDrag(animateHome, event) {
    const layer = this.layers[this.drag.index];
    layer?.classList.remove('is-dragging');
    if (event && event.pointerId != null && typeof event.pointerId === 'number' && layer?.releasePointerCapture) {
      try {
        layer.releasePointerCapture(event.pointerId);
      } catch (err) {
        // Ignore.
      }
    }

    this.layerDrag.forEach((state) => {
      state.tx = 0;
      state.ty = 0;
      state.vx = 0;
      state.vy = 0;
    });
    this.homeTween = {
      startedAt: performance.now(),
      duration: animateHome ? 1800 : 420,
      from: this.layerDrag.map((state) => ({ x: state.x, y: state.y })),
    };

    this.resumeFloat(performance.now());
    this.drag.active = false;
    this.drag.captured = false;
    this.drag.index = -1;
    this.drag.pointerId = null;
    this.startSettle();
  }

  easeInOutCubic(t) {
    const x = Math.min(1, Math.max(0, t));
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  }

  applyHomeTween(now) {
    if (!this.homeTween) return false;

    const t = (now - this.homeTween.startedAt) / this.homeTween.duration;
    const e = this.easeInOutCubic(t);
    const done = t >= 1;

    this.layerDrag.forEach((state, index) => {
      const from = this.homeTween.from[index] || { x: 0, y: 0 };
      if (done) {
        state.x = 0;
        state.y = 0;
        state.vx = 0;
        state.vy = 0;
        state.tx = 0;
        state.ty = 0;
        return;
      }
      state.x = from.x * (1 - e);
      state.y = from.y * (1 - e);
      state.vx = 0;
      state.vy = 0;
      state.tx = state.x;
      state.ty = state.y;
    });

    if (done) this.homeTween = null;
    return !done;
  }

  applyRepulsion(dt) {
    const n = this.layerDrag.length;
    const strength = this.physics.repulsion;

    for (let i = 0; i < n; i += 1) {
      for (let j = i + 1; j < n; j += 1) {
        const a = this.layerDrag[i];
        const b = this.layerDrag[j];
        const dx = a.restX + a.x - (b.restX + b.x);
        const dy = a.restY + a.y - (b.restY + b.y);
        const dist = Math.hypot(dx, dy) || 0.001;
        const rest = Math.max(Math.hypot(a.restX - b.restX, a.restY - b.restY), 48);
        if (dist >= rest) continue;

        const overlap = (rest - dist) / rest;
        const accel = overlap * overlap * strength;
        const nx = dx / dist;
        const ny = dy / dist;
        const heldI = this.drag.active && this.drag.index === i;
        const heldJ = this.drag.active && this.drag.index === j;

        if (!heldI) {
          a.vx += nx * accel * dt;
          a.vy += ny * accel * dt;
        }
        if (!heldJ) {
          b.vx -= nx * accel * dt;
          b.vy -= ny * accel * dt;
        }
      }
    }
  }

  integrateLayer(state, index, dt) {
    const held = this.drag.active && this.drag.index === index;
    const stiffness = this.physics.dragK;
    const damping = this.physics.dragC;

    state.vx += ((state.tx - state.x) * stiffness - state.vx * damping) * dt;
    state.vy += ((state.ty - state.y) * stiffness - state.vy * damping) * dt;

    const maxSpeed = held ? 2400 : 1600;
    const speed = Math.hypot(state.vx, state.vy);
    if (speed > maxSpeed) {
      state.vx *= maxSpeed / speed;
      state.vy *= maxSpeed / speed;
    }

    state.x += state.vx * dt;
    state.y += state.vy * dt;

    const limit = this.dragLimit * (held ? 1 : 1.35);
    state.x = Math.max(-limit, Math.min(limit, state.x));
    state.y = Math.max(-limit, Math.min(limit, state.y));
  }

  startSettle() {
    this.syncInteracting();
    if (this.rafId) return;
    this.lastSettle = performance.now();
    this.rafId = requestAnimationFrame(this.settle);
  }

  syncInteracting() {
    this.classList.toggle('is-interacting', Boolean(this.drag.active || this.homeTween));
  }

  stopSettle() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.homeTween = null;
    this.classList.remove('is-interacting');
  }

  settle(now) {
    const time = now || performance.now();
    const dt = Math.min(0.033, Math.max(0.008, (time - this.lastSettle) / 1000));
    this.lastSettle = time;

    if (this.enableMouse && !this.drag.active) {
      this.mouse.x += (this.mouse.tx - this.mouse.x) * 0.12;
      this.mouse.y += (this.mouse.ty - this.mouse.y) * 0.12;
    }

    const returning = this.applyHomeTween(time);
    this.syncInteracting();

    if (!returning && this.drag.active && this.layerDrag[this.drag.index]) {
      const held = this.layerDrag[this.drag.index];
      this.layerDrag.forEach((state, index) => {
        if (index === this.drag.index) return;
        const follow = this.followStrength(state.depth, held.depth);
        state.tx = held.x * follow;
        state.ty = held.y * follow;
      });
    }

    if (!returning) {
      this.applyRepulsion(dt);
      this.layerDrag.forEach((dragState, index) => {
        this.integrateLayer(dragState, index, dt);
      });
    }

    let stillMoving = this.drag.active || returning;
    this.layerDrag.forEach((dragState) => {
      if (
        Math.abs(dragState.x) > 0.35 ||
        Math.abs(dragState.y) > 0.35 ||
        Math.abs(dragState.vx) > 6 ||
        Math.abs(dragState.vy) > 6
      ) {
        stillMoving = true;
      }
    });

    if (!this.drag.active && !stillMoving) {
      this.layerDrag.forEach((dragState) => {
        dragState.x = 0;
        dragState.y = 0;
        dragState.vx = 0;
        dragState.vy = 0;
      });
    }

    this.layers.forEach((layer, index) => {
      const depth = parseFloat(layer.dataset.depth || String(3 - index));
      const depthFactor = depth / 3;
      const dragState = this.layerDrag[index];

      if (dragState) {
        layer.style.setProperty('--drag-x', `${dragState.x}px`);
        layer.style.setProperty('--drag-y', `${dragState.y}px`);
      }

      const px =
        this.enableMouse && !this.drag.active
          ? this.mouse.x * this.mouseIntensity * depthFactor
          : 0;
      const py =
        this.enableMouse && !this.drag.active
          ? this.mouse.y * this.mouseIntensity * 0.45 * depthFactor
          : 0;

      if (this.enableMouse && !this.drag.active) {
        layer.style.setProperty('--parallax-x', `${px}px`);
        layer.style.setProperty('--parallax-y', `${py}px`);
      }

      const dragX = dragState ? dragState.x : 0;
      const dragY = dragState ? dragState.y : 0;
      const scrollY = parseFloat(layer.style.getPropertyValue('--scroll-y')) || 0;
      const fy = this.floatY(index, time);
      layer.style.setProperty('--float-y', `${fy}px`);
      this.setLayerTransform(layer, px + dragX, py + scrollY + dragY + fy);
    });

    const mouseDone =
      !this.enableMouse ||
      this.drag.active ||
      (Math.abs(this.mouse.tx - this.mouse.x) < 0.01 && Math.abs(this.mouse.ty - this.mouse.y) < 0.01);

    if (mouseDone && !stillMoving && !(this.enableFloat && this.inView)) {
      this.rafId = null;
      this.syncInteracting();
      return;
    }

    this.rafId = requestAnimationFrame(this.settle);
  }
}

if (!customElements.get('hero-layers')) {
  customElements.define('hero-layers', HeroLayers);
}
